import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { IGigCampaignAdminState, IGigCampaign } from '../types/types';
import type { IThunkAPI } from '@/store/types';
import { gigCampaignAdminApi } from '@/api/admin/gig-campaign';
import type { RootState } from '@/store';
import { ICampaignRatioProfile, IVocabRatioProfile, IVocabRatioType } from '@/store/slices/vocab/types';

const initialState: IGigCampaignAdminState = {
  campaignList: [],
  campaign: null,
  isCampaignError: false,
  isCampaignSuccess: false,
  isCampaignFetching: false,
};

export const addGigCampaign = createAsyncThunk<unknown, IGigCampaign, IThunkAPI>(
  'post/campaign/',
  async (campaign, thunkAPI) => {
    try {
      const response = await gigCampaignAdminApi.saveCampaign(campaign);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getCampaignById = createAsyncThunk<IGigCampaign, number, IThunkAPI>(
  'get/campaign/id',
  async (campaignId, thunkAPI) => {
    try {
      const response = await gigCampaignAdminApi.getCampaignById(campaignId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getLatestCampaignByGigId = createAsyncThunk<IGigCampaign, number, IThunkAPI>(
  'get/campaign/gig/id',
  async (gigId, thunkAPI) => {
    try {
      const response = await gigCampaignAdminApi.getLatestCampaignByGigId(gigId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getCampaignCountryProfileList = createAsyncThunk<ICampaignRatioProfile[], undefined, IThunkAPI>(
  'get/campaign/ratio/profile',
  async (_, thunkAPI) => {
    try {
      const response = await gigCampaignAdminApi.getCampaignCountryProfileList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabRatioTypeList = createAsyncThunk<IVocabRatioType[], undefined, IThunkAPI>(
  'get/vocabratiotype/list',
  async (_, thunkAPI) => {
    try {
      const response = await gigCampaignAdminApi.getVocabRatioTypeList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVocabRatioProfileList = createAsyncThunk<IVocabRatioProfile[], undefined, IThunkAPI>(
  'get/vocabratioprofile/list',
  async (_, thunkAPI) => {
    try {
      const response = await gigCampaignAdminApi.getVocabRatioProfileList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const gigCampaignAdminSlice = createSlice({
  name: 'gigCampaignAdmin',
  initialState,
  reducers: {
    clearGigCampaigns(state) {
      state.campaignList = [];
    },
    clearGigCampaign(state) {
      state.campaign = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(getCampaignById.fulfilled, (state, action) => {
      state.campaign = null;
      if (action.payload && action.payload instanceof Object) {
        state.campaign = action.payload;
      } else {
        state.campaign = null;
      }
    });
  },
});

// actions
export const { clearGigCampaigns, clearGigCampaign } = gigCampaignAdminSlice.actions;

// selectors
export const campaignListAll = (state: RootState): IGigCampaignAdminState['campaignList'] =>
  state.gigCampaignAdmin.campaignList;
export const campaign = (state: RootState): IGigCampaignAdminState['campaign'] => state.gigCampaignAdmin.campaign;

export default gigCampaignAdminSlice.reducer;
